import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { messageService } from '../../services/messageService';
import { useAuth } from '../../context/AuthContext.jsx';
import { 
  Send, 
  Paperclip, 
  X, 
  Bold, 
  Italic, 
  Underline,
  List,
  Link,
  Smile,
  Image,
  FileText,
  Save,
  ArrowLeft,
  Users,
  AlertCircle
} from 'lucide-react';

const ComposeMessage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [attachments, setAttachments] = React.useState([]);
  const [recipients, setRecipients] = React.useState([]);
  const [newRecipient, setNewRecipient] = React.useState('');
  const [formData, setFormData] = React.useState({
    subject: '',
    content: '',
    priority: 'normal',
    isUrgent: false,
    isImportant: false,
    sendToAll: false,
    recipientType: 'individual' // individual, class, all_students, all_teachers, all_parents
  });

  const recipientTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'class', label: 'Class' },
    { value: 'all_students', label: 'All Students' },
    { value: 'all_teachers', label: 'All Teachers' },
    { value: 'all_parents', label: 'All Parents' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
    { value: 'normal', label: 'Normal', color: 'bg-gray-100 text-gray-800' },
    { value: 'high', label: 'High', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
  ];

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const handleAddRecipient = () => {
    if (newRecipient.trim() && !recipients.includes(newRecipient.trim())) {
      setRecipients(prev => [...prev, newRecipient.trim()]);
      setNewRecipient('');
    }
  };

  const handleRemoveRecipient = (recipient) => {
    setRecipients(prev => prev.filter(r => r !== recipient));
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      const draftData = {
        ...formData,
        recipients,
        attachments: attachments.map(att => ({ name: att.name, size: att.size, type: att.type })),
        isDraft: true
      };
      await messageService.saveDraft(draftData);
      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Failed to save draft:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!formData.subject.trim()) {
      alert('Please enter a subject');
      return;
    }

    if (!formData.content.trim()) {
      alert('Please enter message content');
      return;
    }

    if (formData.recipientType === 'individual' && recipients.length === 0) {
      alert('Please add at least one recipient');
      return;
    }

    try {
      setLoading(true);
      
      const messageData = new FormData();
      messageData.append('subject', formData.subject);
      messageData.append('content', formData.content);
      messageData.append('priority', formData.priority);
      messageData.append('isUrgent', formData.isUrgent);
      messageData.append('isImportant', formData.isImportant);
      messageData.append('recipientType', formData.recipientType);
      messageData.append('recipients', JSON.stringify(recipients));
      
      // Add attachments
      attachments.forEach((attachment, index) => {
        messageData.append(`attachments`, attachment.file);
      });

      await messageService.sendMessage(messageData);
      alert('Message sent successfully!');
      navigate('/dashboard/messages');
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">Compose Message</h1>
          <p className="text-gray-600 mt-1">Send messages to students, teachers, and parents</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Compose Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Message Form */}
          <Card>
            <CardHeader>
              <CardTitle>New Message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Recipients */}
              <div>
                <Label htmlFor="recipientType">Send To</Label>
                <div className="flex space-x-2 mt-1">
                  <Select value={formData.recipientType} onValueChange={(value) => setFormData(prev => ({ ...prev, recipientType: value }))}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select recipient type" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipientTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.recipientType === 'individual' && (
                  <div className="mt-2">
                    <div className="flex space-x-2">
                      <Input
                        placeholder="Enter email or name..."
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRecipient())}
                      />
                      <Button onClick={handleAddRecipient} size="sm">
                        <Users className="w-4 h-4" />
                      </Button>
                    </div>
                    {recipients.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {recipients.map((recipient, index) => (
                          <Badge key={index} variant="secondary" className="flex items-center space-x-1">
                            <span>{recipient}</span>
                            <button
                              onClick={() => handleRemoveRecipient(recipient)}
                              className="ml-1 hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter message subject"
                  required
                />
              </div>

              {/* Priority and Flags */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map(priority => (
                        <SelectItem key={priority.value} value={priority.value}>
                          <div className="flex items-center space-x-2">
                            <Badge className={priority.color}>{priority.label}</Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Flags</Label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.isUrgent}
                        onChange={(e) => setFormData(prev => ({ ...prev, isUrgent: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">Urgent</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.isImportant}
                        onChange={(e) => setFormData(prev => ({ ...prev, isImportant: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">Important</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div>
                <Label htmlFor="content">Message Content *</Label>
                
                {/* Rich Text Toolbar */}
                <div className="flex items-center space-x-1 p-2 border border-b-0 rounded-t-md bg-gray-50">
                  <Button variant="ghost" size="sm">
                    <Bold className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Italic className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Underline className="w-4 h-4" />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <Button variant="ghost" size="sm">
                    <List className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Link className="w-4 h-4" />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <Button variant="ghost" size="sm">
                    <Image className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Smile className="w-4 h-4" />
                  </Button>
                </div>
                
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Type your message here..."
                  className="min-h-[200px] rounded-t-none"
                  required
                />
              </div>

              {/* Attachments */}
              <div>
                <Label>Attachments</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <Button asChild variant="outline" size="sm">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Paperclip className="w-4 h-4 mr-2" />
                      Add Files
                    </label>
                  </Button>
                  
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center justify-between p-2 border rounded">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <div>
                              <p className="text-sm font-medium">{attachment.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveAttachment(attachment.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Send Options */}
          <Card>
            <CardHeader>
              <CardTitle>Send Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600">
                <p><strong>From:</strong> {user?.firstName} {user?.lastName}</p>
                <p><strong>Email:</strong> {user?.email}</p>
                <p><strong>Role:</strong> {user?.role}</p>
              </div>
              
              <div className="space-y-2">
                <Button 
                  onClick={handleSend} 
                  disabled={loading || !formData.subject.trim() || !formData.content.trim()}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="w-full"
                >
                  {saving ? (
                    <>
                      <LoadingSpinner />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Draft
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Message Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Subject:</span>
                  <span className="font-medium">{formData.subject || 'No subject'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Priority:</span>
                  <Badge className={priorities.find(p => p.value === formData.priority)?.color}>
                    {priorities.find(p => p.value === formData.priority)?.label}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Recipients:</span>
                  <span>{recipients.length} selected</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Attachments:</span>
                  <span>{attachments.length} files</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 space-y-2">
              <p>• Use clear, descriptive subjects</p>
              <p>• Keep messages concise and professional</p>
              <p>• Use priority flags appropriately</p>
              <p>• Attach relevant documents when needed</p>
              <p>• Save drafts for longer messages</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ComposeMessage;